<template>
	<div class="min-h-screen bg-gradient-to-b from-slate-50 to-white">
		<!-- PC端导航栏 (md以上显示) - 现代风格：毛玻璃+细腻阴影+动效 -->
		<header class="hidden md:block sticky top-0 z-50 bg-white/80 backdrop-blur-xl border-b border-slate-200/90 shadow-[0_1px_0_0_rgba(15,23,42,0.04)] transition-all duration-300">
			<div class="max-w-7xl mx-auto px-6 lg:px-8 py-3.5 flex items-center justify-between">
				<div class="flex items-center gap-2.5 cursor-pointer group" @click="activeTab = 'home'">
					<AppIcon class="w-8 h-8" aria-label="顶部图标" />
					<span class="text-xl font-bold bg-gradient-to-r from-primary-600 to-primary-800 bg-clip-text text-transparent tracking-tight">{{ APP_DISPLAY_NAME }}</span>
				</div>
				<nav class="flex items-center gap-8">
					<a
						v-for="nav in pcNavs"
						:key="nav.key"
						href="#"
						@click.prevent="switchTab(nav.key)"
						:class="[
							'text-slate-600 hover:text-primary-600 font-medium transition-all duration-200 relative py-2 text-[15px]',
							activeTab === nav.key
								? 'text-primary-600 after:absolute after:bottom-0 after:left-0 after:w-full after:h-0.5 after:bg-primary-500 after:rounded-full after:scale-x-100 after:origin-left'
								: 'after:absolute after:bottom-0 after:left-0 after:w-full after:h-0.5 after:bg-primary-500 after:rounded-full after:scale-x-0 after:origin-right after:transition-transform after:duration-300 hover:after:scale-x-100 hover:after:origin-left'
						]"
					>
						{{ nav.label }}
					</a>
				</nav>
				<div class="flex items-center gap-3">
					<el-button
						class="!rounded-full !border-primary-300 !text-primary-600 hover:!bg-primary-50 hover:!border-primary-400 !px-5 !font-medium transition-all"
						@click="goToLogin"
					>
						登录
					</el-button>
					<el-button
						class="!rounded-full !bg-gradient-to-r !from-primary-500 !to-primary-600 !border-0 !text-white !shadow-none hover:!shadow-lg hover:scale-105 transition-all duration-300 !px-6 !font-medium"
						@click="goToFunctionPage"
					>
						免费试用
						<i class="fas fa-arrow-right text-sm"></i>
					</el-button>
				</div>
			</div>
		</header>

		<!-- 移动端顶部导航栏 (md以下显示) -->
		<header class="md:hidden sticky top-0 z-50 bg-gradient-to-r from-primary-500 to-primary-700 shadow-md">
			<div class="flex items-center justify-between px-4 py-3">
				<div class="flex items-center gap-2 cursor-pointer" @click="activeTab = 'home'">
					<div class="w-7 h-7 rounded-md flex items-center justify-center text-white text-sm p-1">
						<AppIcon class="w-full h-full" aria-label="顶部图标" />
					</div>
					<span class="text-white font-bold text-base">{{ APP_DISPLAY_NAME }}</span>
				</div>
				<div class="header-right">
					<a href="#" @click.prevent="goToLogin" class="text-white text-sm">登录</a>
				</div>
			</div>
		</header>

		<!-- 主内容区域 -->
		<main class="pb-0">
			<!-- 首页 -->
			<section id="home" class="page-home">
				<!-- 英雄区域 - 现代渐变 + 装饰光晕 -->
				<div class="hero relative overflow-hidden bg-gradient-to-br from-primary-700 via-primary-600 to-primary-800 text-white">
					<!-- 背景图（移动端/PC 自适应 cover），叠加渐变以保证文字可读性 -->
					<div class="absolute inset-0">
						<div
							class="absolute inset-0 bg-cover bg-no-repeat opacity-80"
							:style="{
								backgroundImage: `url(${heroBgImg})`,
								backgroundPosition: isMdUp ? 'center' : '70% center'
							}"
						></div>
						<div class="absolute inset-0 bg-gradient-to-br from-primary-900/70 via-primary-800/55 to-primary-900/70"></div>
					</div>
					<div class="absolute top-0 right-0 w-96 h-96 bg-white/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/3"></div>
					<div class="absolute bottom-0 left-0 w-80 h-80 bg-primary-400/20 rounded-full blur-3xl translate-y-1/3 -translate-x-1/3"></div>
					<div class="max-w-7xl mx-auto px-4 lg:px-8 py-16 lg:py-24 relative z-10">
						<div class="max-w-3xl">
							<h1 class="text-3xl md:text-5xl lg:text-6xl font-bold mb-5 leading-tight tracking-tight">
								让公文写作
								<br />
								更规范、更高效
							</h1>
							<p class="text-base md:text-xl opacity-90 mb-6 max-w-xl">AI 智能辅助 · 国标格式</p>
							<div class="flex gap-3 flex-wrap mb-8">
								<span class="bg-white/20 backdrop-blur-sm px-4 py-1.5 rounded-full text-sm font-medium">智能写作</span>
								<span class="bg-white/20 backdrop-blur-sm px-4 py-1.5 rounded-full text-sm font-medium">格式校验</span>
								<span class="bg-white/20 backdrop-blur-sm px-4 py-1.5 rounded-full text-sm font-medium">合规导出</span>
							</div>
							<button
								@click="goToFunctionPage"
								class="bg-gold-500 text-amber-950 px-8 py-3 rounded-xl font-bold text-base shadow-xl hover:shadow-2xl transition-all hover:scale-105 transform duration-200 flex items-center gap-2 ring-1 ring-white/35 hover:ring-white/55"
							>
								立即免费试用
								<i class="fas fa-arrow-right text-sm"></i>
							</button>
						</div>
					</div>
				</div>

				<!-- 积分卡片 - 悬浮提升 -->
				<div class="max-w-7xl mx-auto px-4 lg:px-8">
					<div class="bg-white rounded-2xl shadow-xl border border-slate-100 overflow-hidden relative z-20 -mt-8 md:-mt-10 mx-auto max-w-4xl">
						<div class="bg-gradient-to-r from-amber-50 to-amber-100/80 p-5 md:p-6 flex flex-col sm:flex-row items-center justify-between gap-3 md:gap-4">
							<div class="text-center sm:text-left">
								<h3 class="text-primary-600 font-bold text-base md:text-xl">🎁 新用户专享</h3>
								<p class="text-slate-600 text-xs md:text-sm">注册即送 5000 积分，免费体验</p>
							</div>
							<div class="flex items-baseline gap-1 md:gap-2 bg-white/60 px-4 py-2 rounded-2xl">
								<span class="text-3xl md:text-5xl font-bold text-primary-600">5000</span>
								<span class="text-slate-500 text-sm font-medium">积分</span>
							</div>
						</div>
					</div>
				</div>

				<!-- 核心优势 - 现代卡片设计 -->
				<div class="max-w-7xl mx-auto px-4 lg:px-8 py-12 lg:py-16">
					<div class="text-center mb-10">
						<h2 class="text-2xl md:text-4xl font-bold mt-3 text-slate-800">核心优势</h2>
						<p class="text-slate-500 mt-2 max-w-2xl mx-auto">专为政务场景打造，让每一份公文都专业、规范、高效</p>
					</div>
					<div class="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
						<div
							v-for="advantage in advantages"
							:key="advantage.title"
							class="group bg-white rounded-2xl p-6 lg:p-8 text-center shadow-sm hover:shadow-xl transition-all duration-300 border border-slate-100 hover:border-primary-100 hover:-translate-y-1"
						>
							<div
								class="w-16 h-16 bg-gradient-to-br from-primary-500 to-primary-600 rounded-2xl flex items-center justify-center mx-auto mb-4 text-3xl shadow-md group-hover:scale-110 transition-transform duration-300"
							>
								<img :src="advantage.iconImg" :alt="advantage.title" class="w-10 h-10 object-contain" />
							</div>
							<h4 class="font-bold text-lg mb-2 text-slate-800">{{ advantage.title }}</h4>
							<p class="text-slate-500 text-sm leading-relaxed">{{ advantage.desc }}</p>
						</div>
					</div>
				</div>

				<!-- 功能预览卡片 - 增强立体感 -->
				<div class="max-w-7xl mx-auto px-4 lg:px-8">
					<div class="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
						<!-- 智能写作 -->
						<div class="bg-white rounded-2xl shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden border border-slate-100 group">
							<div class="bg-gradient-to-r from-primary-500 to-primary-600 px-5 py-4 text-white">
								<h3 class="font-bold text-lg flex items-center gap-2">
									<img :src="smartWritingImg" alt="智能写作" class="w-6 h-6 object-contain" />
									智能写作
								</h3>
								<p class="text-xs opacity-90">输入主题，30秒生成合规初稿</p>
							</div>
							<div class="p-5">
								<div class="bg-slate-50 rounded-xl p-4 mb-4">
									<div class="text-slate-500 text-xs mb-1 font-medium">输入</div>
									<div class="bg-white border border-slate-200 rounded-lg p-2.5 text-sm">关于开展{{ displayYear }}年度工作总结的通知</div>
									<div class="text-slate-500 text-xs mt-3 mb-1 font-medium">AI 生成</div>
									<div class="bg-white border border-green-200 rounded-lg p-2.5 text-sm">各科室、各部门：为全面总结{{ displayYear }}年度工作...</div>
								</div>
								<div class="flex flex-wrap gap-2">
									<span v-for="type in ['通知', '请示', '报告']" :key="type" class="bg-primary-50 text-primary-600 px-3 py-1 rounded-full text-xs font-medium">{{ type }}</span>
								</div>
							</div>
						</div>

						<!-- 高效润色 -->
						<div class="bg-white rounded-2xl shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden border border-slate-100 group">
							<div class="bg-gradient-to-r from-primary-500 to-primary-600 px-5 py-4 text-white">
								<h3 class="font-bold text-lg flex items-center gap-2">
									<img :src="efficientPolishImg" alt="高效润色" class="w-6 h-6 object-contain" />
									高效润色
								</h3>
								<p class="text-xs opacity-90">一键优化，告别口语化</p>
							</div>
							<div class="p-5">
								<div class="bg-slate-50 rounded-xl p-4 mb-4">
									<div class="text-slate-500 text-xs mb-1 font-medium">原文</div>
									<div class="bg-white border border-red-200 rounded-lg p-2.5 text-sm">这个事儿吧，得赶紧搞一下子...</div>
									<div class="text-slate-500 text-xs mt-3 mb-1 font-medium">润色后</div>
									<div class="bg-white border border-green-200 rounded-lg p-2.5 text-sm">为加快推进相关工作，现就事项通知如下：</div>
								</div>
								<div class="flex flex-wrap gap-2">
									<span v-for="tag in ['润色', '缩写', '扩写']" :key="tag" class="bg-primary-50 text-primary-600 px-3 py-1 rounded-full text-xs font-medium">{{ tag }}</span>
								</div>
							</div>
						</div>

						<!-- 格式校验 -->
						<div class="bg-white rounded-2xl shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden border border-slate-100 group">
							<div class="bg-gradient-to-r from-primary-500 to-primary-600 px-5 py-4 text-white">
								<h3 class="font-bold text-lg flex items-center gap-2">
									<img :src="formatCheckImg" alt="格式校验" class="w-6 h-6 object-contain" />
									格式校验
								</h3>
								<p class="text-xs opacity-90">3秒检测格式、敏感词、标点</p>
							</div>
							<div class="p-5">
								<div class="bg-red-50 rounded-xl p-4 mb-4">
									<p class="text-primary-600 font-semibold text-sm mb-2">⚠️ 发现 6 处问题</p>
									<ul class="text-slate-600 text-xs space-y-1">
										<li>📌 格式错误：2处</li>
										<li>📌 敏感词：1处</li>
										<li>📌 标点：3处</li>
									</ul>
								</div>
								<div class="flex flex-wrap gap-2">
									<span class="bg-primary-50 text-primary-600 px-3 py-1 rounded-full text-xs font-medium">一键修复</span>
									<span class="bg-primary-50 text-primary-600 px-3 py-1 rounded-full text-xs font-medium">导出</span>
								</div>
							</div>
						</div>
					</div>
				</div>

				<!-- 操作流程 - 现代化步骤条 -->
				<div class="max-w-7xl mx-auto px-4 lg:px-8 py-12 lg:py-16">
					<div class="bg-white rounded-2xl shadow-md p-6 lg:p-8 border border-slate-100">
						<div class="flex flex-col md:flex-row items-center justify-between gap-4">
							<div v-for="(step, idx) in flowSteps" :key="idx" class="flex-1 text-center relative w-full">
								<div class="relative z-10">
									<div
										class="w-12 h-12 bg-gradient-to-br from-primary-500 to-primary-600 text-white rounded-full flex items-center justify-center mx-auto text-xl font-bold shadow-md"
									>
										{{ step.num }}
									</div>
									<div class="text-sm font-medium mt-2 text-slate-700">{{ step.text }}</div>
								</div>
								<div v-if="idx < flowSteps.length - 1" class="hidden md:block absolute top-5 left-[60%] w-[80%] h-0.5 bg-gradient-to-r from-primary-200 to-primary-400 -z-0"></div>
								<!-- 移动端连接线：纵向步骤条更明显 -->
								<div v-if="idx < flowSteps.length - 1" class="md:hidden flex justify-center">
									<div class="w-0.5 h-8 bg-gradient-to-b from-primary-200 to-primary-400 rounded-full my-2"></div>
								</div>
							</div>
						</div>
					</div>
				</div>

				<!-- CTA横幅 - 现代渐变卡片 -->
				<div class="max-w-7xl mx-auto px-4 lg:px-8 py-6 lg:py-8">
					<div class="bg-gradient-to-r from-primary-600 to-primary-800 rounded-2xl p-8 lg:p-12 text-center text-white relative overflow-hidden">
						<div class="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full blur-2xl"></div>
						<div class="relative z-10">
							<h3 class="text-xl lg:text-3xl font-bold mb-3">准备好提升公文写作效率了吗？</h3>
							<p class="text-sm lg:text-base opacity-90 mb-6">立即注册，免费体验全部功能</p>
							<button
								@click="goToFunctionPage"
								class="bg-white text-primary-600 px-8 py-3 rounded-xl font-semibold text-base hover:shadow-xl transition-all hover:scale-105 inline-flex items-center gap-2"
							>
								开始免费试用
								<i class="fas fa-arrow-right"></i>
							</button>
						</div>
					</div>
				</div>
			</section>

			<!-- 产品功能页 -->
			<section id="features" class="page-features scroll-mt-24 bg-gradient-to-b from-slate-50 to-white border-t border-slate-100">
				<div class="max-w-7xl mx-auto px-4 lg:px-8 py-12 lg:py-16">
					<div class="text-center mb-10">
						<h2 class="text-2xl md:text-4xl font-bold mt-3 text-slate-800">产品功能</h2>
						<p class="text-slate-500 mt-2">六大核心功能，全方位提升公文写作效率</p>
					</div>
					<div class="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
						<div
							v-for="func in featureList"
							:key="func.title"
							class="bg-white rounded-2xl p-5 lg:p-6 shadow-sm hover:shadow-lg transition-all duration-300 flex gap-4 lg:gap-5 border border-slate-100 group hover:border-primary-100"
						>
							<div
								class="w-12 h-12 lg:w-14 lg:h-14 bg-gradient-to-br from-primary-500 to-primary-600 rounded-xl flex items-center justify-center text-xl lg:text-2xl flex-shrink-0 shadow-md group-hover:scale-105 transition-transform"
							>
								<img :src="func.iconImg" :alt="func.title" class="w-7 h-7 lg:w-8 lg:h-8 object-contain" />
							</div>
							<div>
								<h3 class="font-bold text-lg mb-1 text-slate-800">{{ func.title }}</h3>
								<p class="text-slate-500 text-sm mb-2 leading-relaxed">{{ func.desc }}</p>
								<div class="flex flex-wrap gap-1.5">
									<span v-for="tag in func.tags" :key="tag" class="bg-primary-50 text-primary-600 px-2 py-0.5 rounded-full text-xs font-medium">{{ tag }}</span>
								</div>
							</div>
						</div>
					</div>
				</div>
			</section>

			<!-- 解决方案页 -->
			<section id="solutions" class="page-solutions scroll-mt-24 bg-white border-t border-slate-100">
				<div class="max-w-7xl mx-auto px-4 lg:px-8 py-12 lg:py-16">
					<div class="text-center mb-10">
						<h2 class="text-2xl md:text-4xl font-bold mt-3 text-slate-800">解决方案</h2>
						<p class="text-slate-500 mt-2">针对不同行业，提供定制化解决方案</p>
					</div>
					<div class="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
						<div
							v-for="sol in solutions"
							:key="sol.title"
							class="bg-white rounded-2xl shadow-sm hover:shadow-xl transition-all duration-300 overflow-hidden border border-slate-100 group"
						>
							<div class="bg-gradient-to-r from-primary-500 to-primary-600 px-5 py-4 text-white">
								<h3 class="font-bold text-lg flex items-center gap-2">
									<img :src="sol.iconImg" :alt="sol.title" class="w-7 h-7 object-contain" />
									{{ sol.title }}
								</h3>
							</div>
							<div class="p-5">
								<div class="mb-3">
									<span class="text-slate-400 text-xs font-medium">痛点</span>
									<p class="text-sm text-slate-600 mt-1">{{ sol.pain }}</p>
								</div>
								<div class="mb-3">
									<span class="text-slate-400 text-xs font-medium">方案</span>
									<p class="text-sm text-slate-600 mt-1">{{ sol.solution }}</p>
								</div>
								<div>
									<span class="text-slate-400 text-xs font-medium">效果</span>
									<p class="text-primary-600 font-semibold text-sm mt-1">{{ sol.effect }}</p>
								</div>
							</div>
						</div>
					</div>
				</div>
			</section>

			<!-- 客户案例页 -->
			<section id="cases" class="page-cases scroll-mt-24 bg-gradient-to-b from-slate-50 to-white border-t border-slate-100">
				<div class="max-w-7xl mx-auto px-4 lg:px-8 py-12 lg:py-16">
					<div class="text-center mb-10">
						<h2 class="text-2xl md:text-4xl font-bold mt-3 text-slate-800">客户案例</h2>
						<p class="text-slate-500 mt-2">真实客户反馈，见证产品价值</p>
					</div>
					<div class="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
						<div v-for="c in cases" :key="c.title" class="bg-white rounded-2xl p-5 shadow-sm hover:shadow-lg transition-all duration-300 border border-slate-100">
							<span class="inline-block bg-primary-500 text-white text-xs px-2.5 py-1 rounded-full mb-3">{{ c.tag }}</span>
							<h3 class="font-bold text-lg mb-2 text-slate-800">{{ c.title }}</h3>
							<p class="text-slate-500 text-sm mb-4 leading-relaxed">{{ c.content }}</p>
							<div class="flex gap-4 pt-3 border-t border-slate-100">
								<div v-for="stat in c.stats" :key="stat.label" class="text-center flex-1">
									<div class="text-primary-600 font-bold text-xl">{{ stat.value }}</div>
									<div class="text-slate-400 text-xs">{{ stat.label }}</div>
								</div>
							</div>
						</div>
					</div>
				</div>
			</section>

			<!-- 关于我们页 -->
			<section id="about" class="page-about scroll-mt-24 bg-white border-t border-slate-100">
				<div class="max-w-7xl mx-auto px-4 lg:px-8 py-12 lg:py-16">
					<div class="text-center mb-10">
						<h2 class="text-2xl md:text-4xl font-bold mt-3 text-slate-800">关于我们</h2>
						<p class="text-slate-500 mt-2">专注政务数字化，深耕公文写作场景</p>
					</div>
					<div class="bg-white rounded-2xl p-6 shadow-sm border border-slate-100 mb-6">
						<h3 class="font-bold text-xl mb-3 flex items-center gap-2 text-slate-800">
							<span class="w-1 h-5 bg-primary-500 rounded"></span>
							公司介绍
						</h3>
						<p class="text-slate-600 leading-relaxed">
							我们是一家专注于智能公文写作领域的创新型科技企业，致力于通过人工智能技术与公文写作规范的深度融合，为政府机关、企事业单位及社会组织提供高效、精准、合规的公文写作解决方案。
							<br />
							<br />
							作为行业领先的公文写作平台，我们深耕公文写作场景，精准洞察用户在公文创作过程中的痛点与需求。平台依托自然语言处理、知识图谱等前沿技术，构建了涵盖法定公文、事务文书、礼仪文书等多类型的海量模板库与素材库，支持一键套用、智能填充、格式自动校对等功能，让公文写作从繁琐的格式调整与内容构思中解放出来，效率提升显著。
							<br />
							<br />
							我们始终坚持“合规为先、体验至上”的理念，严格遵循《党政机关公文处理工作条例》《党政机关公文格式》等国家标准，确保每一份生成的公文在内容、格式、用语上都精准合规。
							<br />
							<br />
							凭借专业的技术实力与贴心的服务，服务涵盖政务、金融、教育、医疗等多个领域，成为众多组织公文写作的得力助手。
							未来，我们将持续迭代技术、优化产品，以更智能的功能、更丰富的资源，助力用户实现公文写作的数字化转型，让公文创作更简单、更高效。
						</p>
					</div>
				</div>
			</section>

			<!-- 帮助中心页 -->
			<section v-if="false" id="help" class="page-help scroll-mt-24 bg-gradient-to-b from-slate-50 to-white border-t border-slate-100">
				<div class="max-w-4xl mx-auto px-4 lg:px-8 py-12 lg:py-16">
					<div class="text-center mb-10">
						<h2 class="text-2xl md:text-4xl font-bold text-slate-800">帮助中心</h2>
						<p class="text-slate-500 mt-2">快速上手，解答您的疑问</p>
					</div>
					<div class="bg-white rounded-2xl overflow-hidden shadow-sm border border-slate-100 mb-6">
						<div class="bg-gradient-to-r from-primary-50 to-white px-6 py-3 border-b border-primary-100">
							<h3 class="font-semibold text-primary-700 text-lg flex items-center gap-2">🚀 快速入门</h3>
						</div>
						<div class="divide-y divide-slate-100">
							<div v-for="item in quickStartItems" :key="item" class="px-6 py-4 flex justify-between items-center text-sm group cursor-pointer hover:bg-slate-50 transition-colors">
								<span class="text-slate-700 group-hover:text-primary-600 font-medium">{{ item }}</span>
								<span class="text-slate-400 group-hover:text-primary-500 transition-transform group-hover:translate-x-1">›</span>
							</div>
						</div>
					</div>
					<div class="bg-white rounded-2xl overflow-hidden shadow-sm border border-slate-100">
						<div class="bg-gradient-to-r from-primary-50 to-white px-6 py-3 border-b border-primary-100">
							<h3 class="font-semibold text-primary-700 text-lg flex items-center gap-2">💡 常见问题</h3>
						</div>
						<div class="divide-y divide-slate-100">
							<div v-for="item in faqItems" :key="item" class="px-6 py-4 flex justify-between items-center text-sm group cursor-pointer hover:bg-slate-50 transition-colors">
								<span class="text-slate-700 group-hover:text-primary-600 font-medium">{{ item }}</span>
								<span class="text-slate-400 group-hover:text-primary-500 transition-transform group-hover:translate-x-1">›</span>
							</div>
						</div>
					</div>
				</div>
			</section>

			<!-- 法律合规页 -->
			<section id="law" class="page-law scroll-mt-24 bg-white border-t border-slate-100">
				<div class="max-w-4xl mx-auto px-4 lg:px-8 py-12 lg:py-16">
					<div class="text-center mb-10">
						<h2 class="text-2xl md:text-4xl font-bold text-slate-800">法律合规</h2>
						<p class="text-slate-500 mt-2">透明合规，保障您的权益</p>
					</div>
					<div class="bg-white rounded-2xl p-6 shadow-sm border border-slate-100 mb-6">
						<div class="flex items-center justify-between flex-wrap gap-3">
							<div>
								<h4 class="font-bold text-lg mb-2 text-slate-800">📄 隐私政策</h4>
								<p class="text-slate-500 text-sm leading-relaxed">我们严格保护用户数据安全，收集范围仅限于产品使用必要信息。</p>
							</div>
							<a
								:href="'https://' + OFFICIAL_WEBSITE + '/privacy'"
								target="_blank"
								class="text-primary-500 text-sm hover:text-primary-700 font-medium flex items-center gap-1 transition-colors whitespace-nowrap"
							>
								查看完整政策
								<i class="fas fa-external-link-alt text-xs"></i>
							</a>
						</div>
					</div>
					<div class="bg-white rounded-2xl p-6 shadow-sm border border-slate-100">
						<div class="flex items-center justify-between flex-wrap gap-3">
							<div>
								<h4 class="font-bold text-lg mb-2 text-slate-800">📄 服务协议</h4>
								<p class="text-slate-500 text-sm leading-relaxed">服务范围包含AI写作辅助、格式校验等功能，用户需对生成内容负责，不得用于违法违规用途。</p>
							</div>
							<a
								:href="'https://' + OFFICIAL_WEBSITE + '/agreement'"
								target="_blank"
								class="text-primary-500 text-sm hover:text-primary-700 font-medium flex items-center gap-1 transition-colors whitespace-nowrap"
							>
								查看完整协议
								<i class="fas fa-external-link-alt text-xs"></i>
							</a>
						</div>
					</div>
				</div>
			</section>
		</main>

		<!-- 移动端悬浮侧边导航（可展开/自动隐藏） -->
		<div class="md:hidden">
			<!-- 展开时的遮罩，点击空白关闭 -->
			<div v-if="mobileNavOpen" class="fixed inset-0 z-[60]" @click="mobileNavOpen = false"></div>

			<!-- 悬浮球导航：默认半藏在边缘，点击滑出 -->
			<div class="fixed top-1/2 -translate-y-1/2 z-[70] transition-transform duration-300" :class="mobileNavOpen ? 'right-2 translate-x-0' : 'right-[-32px] translate-x-0'">
				<div class="flex items-center">
					<!-- 悬浮球按钮 -->
					<button
						class="w-10 h-10 rounded-full bg-white/95 backdrop-blur border border-slate-200 shadow-lg flex items-center justify-center text-slate-700 text-sm mr-1"
						@click.stop="mobileNavOpen = !mobileNavOpen"
						aria-label="切换导航"
					>
						☰
					</button>

					<!-- 展开状态：导航面板 -->
					<div v-show="mobileNavOpen" class="w-44 rounded-2xl bg-white/95 backdrop-blur border border-slate-200 shadow-xl overflow-hidden" @click.stop>
						<div class="px-3 py-2 text-xs font-semibold text-slate-500 border-b border-slate-100">快速导航</div>
						<div class="p-2 flex flex-col gap-1">
							<button
								v-for="item in mobileNavItems"
								:key="item.key"
								class="text-left px-3 py-2 rounded-xl text-sm font-medium transition-colors"
								:class="activeTab === item.key ? 'bg-primary-50 text-primary-700' : 'text-slate-700 hover:bg-slate-50'"
								@click="scrollToSection(item.key)"
							>
								{{ item.label }}
							</button>
						</div>
					</div>
				</div>
			</div>
		</div>

		<!-- PC端页脚 -->
		<footer class="hidden md:block bg-slate-900 text-white py-12 mt-8">
			<div class="max-w-7xl mx-auto px-6 lg:px-8">
				<div class="grid grid-cols-4 gap-8 mb-8">
					<div>
						<h3 class="text-xl font-bold mb-4 flex items-center gap-2">
							<span class="w-8 h-8 rounded-md flex items-center justify-center text-sm p-1">
								<AppIcon class="w-full h-full" aria-label="底部图标" />
							</span>
							{{ APP_DISPLAY_NAME }}
						</h3>
						<p class="text-slate-400 text-sm">
							AI智能公文写作平台
							<br />
							让公文写作更规范、更高效
						</p>
					</div>
					<div>
						<h4 class="font-semibold mb-4 text-slate-200">产品</h4>
						<ul class="space-y-2 text-slate-400 text-sm">
							<li class="cursor-pointer hover:text-primary-400 transition-colors" @click="goToHomePage('writing')">智能写作</li>
							<li class="cursor-pointer hover:text-primary-400 transition-colors" @click="goToHomePage('polish')">公文润色</li>
							<li class="cursor-pointer hover:text-primary-400 transition-colors" @click="goToHomePage('check')">格式校验</li>
						</ul>
					</div>
					<div>
						<h4 class="font-semibold mb-4 text-slate-200">支持</h4>
						<ul class="space-y-2 text-slate-400 text-sm">
							<li class="cursor-pointer hover:text-primary-400 transition-colors" @click="goToUserManual">使用手册</li>
						</ul>
					</div>
					<div>
						<h4 class="font-semibold mb-4 text-slate-200">法律</h4>
						<ul class="space-y-2 text-slate-400 text-sm">
							<li class="cursor-pointer hover:text-primary-400 transition-colors" @click="(switchTab('law'), goToPrivacyPage())">隐私政策</li>
							<li class="cursor-pointer hover:text-primary-400 transition-colors" @click="(switchTab('law'), goToAgreementPage())">服务协议</li>
						</ul>
					</div>
				</div>
				<div class="pt-6 border-t border-slate-800 text-center text-slate-500 text-sm">
					<p><a href="http://beian.miit.gov.cn" class="hover:text-white transition-colors">{{ ICP_FILING }}</a></p>
				</div>
			</div>
		</footer>
	</div>
</template>

<script setup lang="ts">
import { APP_DISPLAY_NAME, ICP_FILING, OFFICIAL_WEBSITE } from '@/config/app'
import AppIcon from '@/components/common/AppIcon.vue'
import { computed, onMounted, onUnmounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import heroBgImg from '@/assets/image/web/bg.jpg'
import aiDocWritingImg from '@/assets/image/web/ai-doc-writing.png'
import docPolishImg from '@/assets/image/web/doc-polish.png'
import shortenExpandImg from '@/assets/image/web/shorten-expand.png'
import docQuotesImg from '@/assets/image/web/doc-quotes.png'
import standardExportImg from '@/assets/image/web/standard-export.png'
import smartWritingImg from '@/assets/image/web/smart-writing.png'
import efficientPolishImg from '@/assets/image/web/efficient-polish.png'
import formatCheckImg from '@/assets/image/web/format-check.png'
import docCheckImg from '@/assets/image/web/doc-check.png'
import partyGovernmentImg from '@/assets/image/web/party-government.png'
import stateOwnedEnterpriseImg from '@/assets/image/web/state-owned-enterprise.png'
import publicInstitutionImg from '@/assets/image/web/public-institution.png'
import complianceImg from '@/assets/image/web/compliance.png'
import efficiencyImg from '@/assets/image/web/efficiency.png'
import traceableImg from '@/assets/image/web/traceable.png'

const router = useRouter()

const activeTab = ref('home')
const isMdUp = ref(false)
const mobileNavOpen = ref(false)
const currentYear = new Date().getFullYear()
const displayYear = computed(() => currentYear)

function updateIsMdUp() {
	isMdUp.value = window.matchMedia('(min-width: 768px)').matches
}

const mobileNavItems = computed(() => [
	...pcNavs,
	//{ key: 'help', label: '帮助中心' },
	{ key: 'law', label: '法律合规' }
])

function scrollToSection(sectionId: string) {
	activeTab.value = sectionId
	const el = document.getElementById(sectionId)
	if (el) {
		el.scrollIntoView({ behavior: 'smooth', block: 'start' })
	} else {
		window.scrollTo({ top: 0, behavior: 'smooth' })
	}
	mobileNavOpen.value = false
}

let sectionObserver: IntersectionObserver | null = null

onMounted(() => {
	updateIsMdUp()
	window.addEventListener('resize', updateIsMdUp)

	// PC 端滚动联动导航高亮（scrollspy）
	const sectionIds = ['home', 'features', 'solutions', 'cases', 'about', 'help', 'law']
	const observedEls = sectionIds.map(id => document.getElementById(id)).filter(Boolean) as HTMLElement[]

	if (observedEls.length) {
		sectionObserver = new IntersectionObserver(
			entries => {
				// 选取当前可见度最高的 section 作为激活项
				let best: { id: string; ratio: number } | null = null
				for (const entry of entries) {
					if (!entry.isIntersecting) continue
					const id = (entry.target as HTMLElement).id
					const ratio = entry.intersectionRatio
					if (!best || ratio > best.ratio) best = { id, ratio }
				}

				if (!best) return
				const pcKeys = new Set(pcNavs.map(n => n.key))
				activeTab.value = pcKeys.has(best.id) ? best.id : ''
			},
			{
				// 预留 sticky header 的空间，让激活更符合“看到标题就高亮”
				root: null,
				rootMargin: '-20% 0px -70% 0px',
				threshold: [0.1, 0.2, 0.35, 0.5, 0.65, 0.8]
			}
		)

		for (const el of observedEls) sectionObserver.observe(el)
	}
})

onUnmounted(() => {
	window.removeEventListener('resize', updateIsMdUp)
	if (sectionObserver) {
		sectionObserver.disconnect()
		sectionObserver = null
	}
})

const pcNavs = [
	{ key: 'home', label: '首页' },
	{ key: 'features', label: '产品功能' },
	{ key: 'solutions', label: '解决方案' },
	{ key: 'cases', label: '客户案例' },
	{ key: 'about', label: '关于我们' }
]

const advantages = [
	{ iconImg: complianceImg, title: '合规', desc: '严格遵循 GB/T 9704-2012 党政机关公文格式标准' },
	{ iconImg: efficiencyImg, title: '高效', desc: '输入主题，分钟级生成高质量初稿，告别加班赶稿' },
	{ iconImg: traceableImg, title: '智能', desc: '一键排版、自动纠错、公文用语润色，降低返工率' }
]

const partners = ['某市政府', '某区财政局', '某省国企', '某市教育局']
const flowSteps = [
	{ num: 1, text: '输入需求' },
	{ num: 2, text: 'AI生成' },
	{ num: 3, text: '在线润色' },
	{ num: 4, text: '导出' }
]

const featureList = [
	{ iconImg: aiDocWritingImg, title: 'AI 公文写作', desc: '智能生成完整初稿，支持15+公文类型，自动遵循GB/T 9704格式规范。', tags: ['通知', '请示', '报告'] },
	{ iconImg: docPolishImg, title: '公文润色', desc: '一键优化用词、句式，去除口语化，替换规范用语。', tags: ['语句优化', '用词规范'] },
	{ iconImg: shortenExpandImg, title: '缩写扩写', desc: '长文压缩为摘要，或根据提纲扩展为完整段落。', tags: ['智能缩写', '段落扩写'] },
	{ iconImg: docQuotesImg, title: '公文金句', desc: '标题、开头、结尾精准表达推荐，15+文种专属金句库。', tags: ['标题金句', '话术推荐'] },
	{ iconImg: docCheckImg, title: '公文校验', desc: '自动检测格式错误、敏感词、标点问题，一键修复。', tags: ['格式检查', '敏感词'] },
	{ iconImg: standardExportImg, title: '标准导出', desc: '导出Word/PDF，严格遵循国标公文格式。', tags: ['Word', 'PDF'] }
]

const solutions = [
	{
		iconImg: partyGovernmentImg,
		title: '党政机关',
		pain: '公文量大、格式要求严、时效性强、人员变动频繁',
		solution: 'AI初稿生成 + 格式规范',
		effect: '写作效率提升 80%，格式合规率 100%'
	},
	{ iconImg: stateOwnedEnterpriseImg, title: '国有企业', pain: '汇报材料多、写作效率低', solution: '素材库 + 历史复用', effect: '写作效率提升 60%，报告周期缩短 70%' },
	{
		iconImg: publicInstitutionImg,
		title: '事业单位',
		pain: '专业术语多、规范性要求高、新人上手慢',
		solution: '专业模板库 + 知识沉淀 + AI辅助',
		effect: '上手周期缩短 70%，术语错误减少 90%'
	}
]

const cases = [
	{
		tag: '党政机关',
		title: '某审批局',
		content: '公文格式问题曾导致反复退回。AI辅助生成与校验后，退回率稳定在1%以下，单份处理时间缩短60%。',
		stats: [
			{ value: '60%', label: '效率提升' },
			{ value: '1%', label: '退回率' }
		]
	},
	{
		tag: '国有企业',
		title: '某国有企业',
		content: '年度工作报告原需 10 天 左右，多部门反复修改。AI 辅助后 3 天 完成，周期缩短 70%。',
		stats: [
			{ value: '3天', label: '报告周期' },
			{ value: '70%', label: '周期缩短' }
		]
	},
	{
		tag: '事业单位',
		title: '某道办事处',
		content: '基层人员流动大，新人原需 2-3 周 才能独立成文。AI 辅助后 3-5 天 即可达标，上手时间缩短 70%，公文达标率达 98%。',
		stats: [
			{ value: '98%', label: '达标率' },
			{ value: '70%', label: '上手加快' }
		]
	}
]

const quickStartItems = ['注册与登录', '积分系统说明', '新手任务攻略']
const faqItems = ['积分用完了怎么办？', '生成内容如何优化？', '如何导出标准格式？']

const switchTab = (tabKey: string) => {
	if (!pcNavs.some(n => n.key === tabKey)) return
	scrollToSection(tabKey)
}

const goToFunctionPage = () => router.push('/zhuli')
const openRouteInNewTab = (path: string) => {
	const route = router.resolve(path)
	window.open(route.href, '_blank')
}
const goToPrivacyPage = () => openRouteInNewTab('/privacy')
const goToAgreementPage = () => openRouteInNewTab('/agreement')
const goToUserManual = () => openRouteInNewTab('/user-manual')
const goToLogin = () => router.push('/zhuli?action=login')
const goToHomePage = (action: string) => router.push('/zhuli?action=' + action)
</script>

<style scoped>
.bg-gold-500 {
	background-color: #f5b042;
}
.text-gold-500 {
	color: #f5b042;
}
</style>
